import { useEffect, useState } from "react";

import { styled } from "@mui/material/styles";
import { useVirtualizer } from "@tanstack/react-virtual";

import { OptionsList } from "./shared.styles";

const VIRTUALIZE_THRESHOLD = 100;
const ESTIMATED_ROW_HEIGHT = 37;
const OVERSCAN = 8;

type MeasureRef = (element: Element | null) => void;

type OptionListProps = {
  count: number;
  focusedIndex: number;
  renderOption: (index: number, measureRef?: MeasureRef) => React.ReactNode;
  "aria-labelledby"?: string;
  "aria-multiselectable"?: boolean;
  id?: string;
  role?: string;
};

const OptionList = ({
  count,
  focusedIndex,
  renderOption,
  ...listProps
}: OptionListProps): React.ReactElement => {
  if (count <= VIRTUALIZE_THRESHOLD) {
    return (
      <OptionsList {...listProps}>
        {Array.from({ length: count }, (_, index) => renderOption(index))}
      </OptionsList>
    );
  }

  return (
    <VirtualOptionList
      count={count}
      focusedIndex={focusedIndex}
      renderOption={renderOption}
      {...listProps}
    />
  );
};

const VirtualOptionList = ({
  count,
  focusedIndex,
  renderOption,
  ...listProps
}: OptionListProps): React.ReactElement => {
  const [scrollElement, setScrollElement] = useState<HTMLUListElement | null>(
    null,
  );

  const virtualizer = useVirtualizer({
    count,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    getScrollElement: () => scrollElement,
    overscan: OVERSCAN,
  });

  useEffect(() => {
    if (focusedIndex >= 0) {
      virtualizer.scrollToIndex(focusedIndex, { align: "auto" });
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [focusedIndex]);

  const items = virtualizer.getVirtualItems();
  const spaceBefore = items[0]?.start ?? 0;
  const spaceAfter = virtualizer.getTotalSize() - (items.at(-1)?.end ?? 0);

  return (
    <OptionsList ref={setScrollElement} {...listProps}>
      <Spacer aria-hidden role="presentation" style={{ height: spaceBefore }} />
      {items.map((item) =>
        renderOption(item.index, virtualizer.measureElement),
      )}
      <Spacer aria-hidden role="presentation" style={{ height: spaceAfter }} />
    </OptionsList>
  );
};

const Spacer = styled("li")({
  listStyle: "none",
  margin: 0,
  padding: 0,
});

export { OptionList, type MeasureRef };
